/**
 * SEO Service Registry — single source of truth for Shashio's service landing pages.
 *
 * Every service page, its metadata, JSON-LD structured data, the sitemap, and
 * internal linking are all derived from this registry. Adding a new service here
 * (plus a thin route that renders <ServiceLandingClient>) is enough to publish a
 * fully SEO-optimised, indexable landing page.
 */

export const SITE_URL = 'https://www.shashio.com';
export const BRAND = 'Shashio';

export interface ServiceFaq {
  q: string;
  a: string;
}

export interface BeforeAfterPair {
  before: string;
  after: string;
  label: string;
}

export interface ServiceBenefit {
  title: string;
  description: string;
  icon: string; // emoji or short glyph
}

export interface SeoService {
  /** Route slug under /services/<slug>. Clean, keyword-rich, no query params. */
  slug: string;
  /** Short display name (nav, cards, breadcrumbs). */
  name: string;
  /** Page H1. */
  h1: string;
  /** <title> — keep under ~60 chars where possible. */
  metaTitle: string;
  /** Meta description — 140–160 chars, action oriented. */
  metaDescription: string;
  keywords: string[];
  priceFrom: string;
  priceUnit: string;
  turnaround: string;
  formats: string[];
  /** One-line hero subheading. */
  tagline: string;
  /** "What the service is" paragraph. */
  intro: string;
  /** "Why photographers need it" paragraph. */
  whyItMatters: string;
  benefits: ServiceBenefit[];
  beforeAfter: BeforeAfterPair[];
  faqs: ServiceFaq[];
  /** Related service slugs for internal linking. */
  related: string[];
  /** Absolute or root-relative OG image. */
  ogImage: string;
  /** Sitemap priority + change frequency. */
  priority: number;
}

const G = (name: string) => `/images/gallery/${name}`;

export const SERVICES: Record<string, SeoService> = {
  'hdr-photo-editing': {
    slug: 'hdr-photo-editing',
    name: 'HDR Photo Editing',
    h1: 'HDR real estate photo editing',
    metaTitle: 'HDR Real Estate Photo Editing | Shashio',
    metaDescription:
      'Professional HDR real estate photo editing from bracketed exposures. Balanced windows, natural light, and true-to-life colour with a 4–12 hour turnaround from $1.20/image.',
    keywords: [
      'HDR real estate photo editing',
      'HDR photo editing',
      'real estate HDR blending',
      'bracketed exposure editing',
      'window pull editing',
      'flambient editing',
    ],
    priceFrom: '$1.20',
    priceUnit: 'per HDR image',
    turnaround: '4–12 hours',
    formats: ['RAW (CR2, CR3, NEF, ARW, DNG, RAF)', 'JPG', 'TIFF', 'PNG'],
    tagline:
      'Turn your bracketed exposures into clean, natural, magazine-ready listing photos — no halos, no blown windows.',
    intro:
      'HDR (High Dynamic Range) editing blends several bracketed exposures of the same scene into a single, perfectly balanced photograph. Our editors merge your brackets by hand, pull the view through every window, and colour-correct the result so interiors look bright and inviting while exteriors stay crisp and detailed.',
    whyItMatters:
      'A single exposure can rarely capture both a bright window and a well-lit room. HDR fixes that. For real estate photographers, professionally blended HDR means faster shoots, fewer reshoots, and listings that look like they belong in a magazine — which helps agents win listings and sell faster.',
    benefits: [
      { title: 'Perfect window pulls', description: 'Every window shows the real view outside — never blown-out white.', icon: '🪟' },
      { title: 'Natural, true-to-life colour', description: 'Accurate whites and warm, inviting interiors without the "HDR look".', icon: '🎨' },
      { title: 'Hand-blended, not automated', description: 'Real editors merge your brackets — no ghosting, halos, or artefacts.', icon: '✋' },
      { title: '4–12 hour turnaround', description: 'Upload today, download finished images tomorrow. Rush available.', icon: '⚡' },
    ],
    beforeAfter: [
      { before: G('enhancement-interior-before.webp'), after: G('enhancement-interior-after.webp'), label: 'Interior window pull' },
      { before: G('enhancement-exterior-before.webp'), after: G('enhancement-exterior-after.webp'), label: 'Exterior HDR blend' },
      { before: G('enbf.webp'), after: G('enaf.webp'), label: 'Living room HDR' },
    ],
    faqs: [
      { q: 'What is HDR processing?', a: 'HDR (High Dynamic Range) processing blends multiple photos of the same scene taken at different exposures into one balanced image. This captures detail in both the bright areas (like windows) and the dark areas (like shadowed corners) that a single photo cannot.' },
      { q: 'How many exposures should I upload?', a: 'Three to seven bracketed exposures per scene works best. Three (under, normal, over) is the sweet spot for most interiors. Our estimator automatically groups your uploads into HDR sets so you can see the count and price instantly.' },
      { q: 'Do you support RAW files?', a: 'Yes. We accept all major RAW formats — Canon (CR2, CR3), Nikon (NEF), Sony (ARW), Adobe (DNG), Fujifilm (RAF) — plus JPG, TIFF, and PNG. RAW gives our editors the most latitude for the cleanest result.' },
      { q: 'What turnaround time do you offer?', a: 'Most orders are delivered in 4 to 12 hours on average — often the same day you upload.' },
      { q: 'Will the photos look over-processed?', a: 'No. We specialise in a natural, true-to-life finish — balanced light and accurate colour without halos, over-saturation, or the artificial "HDR look". If you prefer a brighter or more dramatic style, just tell us in your order preferences.' },
      { q: 'How much does HDR editing cost?', a: 'HDR editing starts at $1.20 per finished image, with volume pricing for larger orders. You only pay after you approve the results, and your final price is never higher than the estimate shown at checkout.' },
    ],
    related: ['day-to-dusk', 'item-removal', 'virtual-staging', 'sky-replacement'],
    ogImage: G('enhancement-interior-after.webp'),
    priority: 0.9,
  },

  'day-to-dusk': {
    slug: 'day-to-dusk',
    name: 'Day to Dusk',
    h1: 'Day to dusk photo editing',
    metaTitle: 'Day to Dusk Real Estate Photo Editing | Shashio',
    metaDescription:
      'Transform daytime property photos into stunning twilight hero shots. Realistic dusk skies, warm lighting, and lawn enhancement from $4.00/image, delivered in 4–12 hours.',
    keywords: ['day to dusk editing', 'twilight photo editing', 'dusk conversion real estate', 'real estate hero shot'],
    priceFrom: '$4.00',
    priceUnit: 'per image',
    turnaround: '4–12 hours',
    formats: ['JPG', 'TIFF', 'PNG', 'RAW'],
    tagline: 'Turn flat daytime shots into warm, golden-hour twilight scenes that make listings unforgettable.',
    intro:
      'Day to dusk editing converts an ordinary daytime exterior into a breathtaking twilight hero shot — replacing the sky with a realistic sunset, warming the lighting, and turning on interior and exterior lights for that magazine-cover glow.',
    whyItMatters:
      'Dusk photos are the single most eye-catching image in a listing. They create an emotional connection, stop the scroll, and give agents a hero shot without waiting on-site for the perfect sunset.',
    benefits: [
      { title: 'Realistic dusk skies', description: 'Natural sunset gradients matched to the scene and time of day.', icon: '🌅' },
      { title: 'Warm, inviting lighting', description: 'Interior and exterior lights turned on for a welcoming glow.', icon: '💡' },
      { title: 'Lawn & water enhancement', description: 'Green lawns and vibrant water features included.', icon: '🌱' },
      { title: '4–12 hour turnaround', description: 'Fast delivery so you never miss a listing deadline.', icon: '⚡' },
    ],
    beforeAfter: [
      { before: G('day-dusk-residential-before.webp'), after: G('day-dusk-residential-after.webp'), label: 'Residential twilight' },
      { before: G('2ddbf.webp'), after: G('2ddaf.webp'), label: 'Exterior dusk' },
      { before: G('day-dusk-commercial-before.webp'), after: G('day-dusk-commercial-after.webp'), label: 'Commercial dusk' },
    ],
    faqs: [
      { q: 'How long does day to dusk editing take?', a: 'Most day to dusk edits are delivered in 4 to 12 hours on average.' },
      { q: 'Can you turn on the interior lights?', a: 'Yes — turning on interior and exterior lighting is included in every day to dusk edit for that warm, welcoming look.' },
      { q: 'What if the sky in my photo is grey?', a: 'No problem. We replace the sky entirely with a realistic dusk gradient, so a flat or overcast sky becomes a stunning sunset.' },
    ],
    related: ['hdr-photo-editing', 'sky-replacement', 'item-removal', 'virtual-staging'],
    ogImage: G('day-dusk-residential-after.webp'),
    priority: 0.8,
  },

  'virtual-staging': {
    slug: 'virtual-staging',
    name: 'Virtual Staging',
    h1: 'Virtual staging for real estate',
    metaTitle: 'Virtual Staging Services for Real Estate | Shashio',
    metaDescription:
      'Photorealistic virtual staging that furnishes empty rooms in any style. Help buyers picture themselves at home from $18.00/image with a fast turnaround.',
    keywords: ['virtual staging', 'virtual staging services', 'furnish empty rooms', 'real estate staging'],
    priceFrom: '$18.00',
    priceUnit: 'per image',
    turnaround: '24–48 hours',
    formats: ['JPG', 'TIFF', 'PNG'],
    tagline: 'Furnish empty rooms with photorealistic, style-matched decor so buyers can picture themselves at home.',
    intro:
      'Virtual staging digitally furnishes empty or dated rooms with photorealistic furniture and decor. Choose from modern, Scandinavian, luxury, and more — matched to the room and lighting so it looks completely natural.',
    whyItMatters:
      'Empty rooms feel small and forgettable. Staged rooms sell. Virtual staging costs a fraction of physical staging and helps listings attract more clicks and faster offers.',
    benefits: [
      { title: 'Photorealistic furniture', description: 'Style-matched decor that looks real, not pasted-on.', icon: '🛋️' },
      { title: 'Any style you want', description: 'Modern, Scandinavian, luxury, farmhouse and more.', icon: '🎨' },
      { title: 'A fraction of physical staging', description: 'Stage a whole home for the cost of one physical room.', icon: '💰' },
      { title: 'Fast turnaround', description: 'Staged images back in 24–48 hours.', icon: '⚡' },
    ],
    beforeAfter: [
      { before: G('staging-living-before.webp'), after: G('staging-living-after.webp'), label: 'Living room staging' },
      { before: G('vsbefore.webp'), after: G('vsafter.webp'), label: 'Empty to furnished' },
      { before: G('2vsbf.webp'), after: G('2vsaf.webp'), label: 'Bedroom staging' },
    ],
    faqs: [
      { q: 'Is virtual staging legal to use in listings?', a: 'Yes, as long as you disclose that images are virtually staged. Most MLS boards require a simple caption such as "virtually staged", which we recommend.' },
      { q: 'Can you match a specific style?', a: 'Absolutely. Tell us the style you want — modern, luxury, Scandinavian, farmhouse — and our editors match furniture and decor to the room.' },
      { q: 'What do you need from me?', a: 'A clear, well-lit photo of the empty room. HDR-blended or enhanced images give the best staging results.' },
    ],
    related: ['virtual-renovation', 'hdr-photo-editing', 'item-removal', 'day-to-dusk'],
    ogImage: G('staging-living-after.webp'),
    priority: 0.8,
  },

  'item-removal': {
    slug: 'item-removal',
    name: 'Item Removal',
    h1: 'Item & object removal for real estate photos',
    metaTitle: 'Object & Item Removal for Real Estate Photos | Shashio',
    metaDescription:
      'Remove clutter, cars, wires, and distractions from property photos for clean, sale-ready images. Professional object removal from $4.00/image in 4–12 hours.',
    keywords: ['item removal real estate', 'object removal', 'photo clutter removal', 'car removal real estate photo'],
    priceFrom: '$4.00',
    priceUnit: 'per image',
    turnaround: '4–12 hours',
    formats: ['JPG', 'TIFF', 'PNG', 'RAW'],
    tagline: 'Remove clutter, cars, wires, and distractions for spotless, sale-ready property photos.',
    intro:
      'Item removal cleans up anything that distracts from the property — vehicles, bins, cables, personal items, signage, and reflections — filling the space naturally so the photo looks like nothing was ever there.',
    whyItMatters:
      'Buyers judge listings in seconds. A stray bin or tangle of cords pulls attention away from the home. Clean photos keep the focus on the property and its best features.',
    benefits: [
      { title: 'Seamless removals', description: 'Objects removed with natural, invisible fills.', icon: '🧹' },
      { title: 'Cars & clutter gone', description: 'Driveways, streets, and rooms cleared of distractions.', icon: '🚗' },
      { title: 'Wires & reflections', description: 'Power lines, cords, and photographer reflections removed.', icon: '⚡' },
      { title: '4–12 hour turnaround', description: 'Fast, reliable delivery every time.', icon: '⏱️' },
    ],
    beforeAfter: [
      { before: G('item-removal-living-before.webp'), after: G('item-removal-living-after.webp'), label: 'Living room cleanup' },
      { before: G('item-removal-bedroom-before.webp'), after: G('item-removal-bedroom-after.webp'), label: 'Bedroom cleanup' },
      { before: G('minorbf.webp'), after: G('minoraf.webp'), label: 'Minor distraction removal' },
    ],
    faqs: [
      { q: 'What can you remove from a photo?', a: 'Almost anything — cars, bins, cables, wall marks, personal photos, signage, reflections, and clutter. If you can point it out, we can usually remove it.' },
      { q: 'How much does object removal cost?', a: 'Simple removals start at $4.00 per image. Complex removals (large objects or multiple items) may vary; you always approve the price before paying.' },
      { q: 'Will the edit look natural?', a: 'Yes. Our editors reconstruct the background by hand so the removed area blends seamlessly with the surrounding scene.' },
    ],
    related: ['hdr-photo-editing', 'virtual-staging', 'image-enhancement', 'day-to-dusk'],
    ogImage: G('item-removal-living-after.webp'),
    priority: 0.8,
  },

  'image-enhancement': {
    slug: 'image-enhancement',
    name: 'Image Enhancement',
    h1: 'Real estate photo enhancement',
    metaTitle: 'Real Estate Photo Enhancement Services | Shashio',
    metaDescription:
      'A comprehensive photo enhancement: colour correction, sharpening, straightening, sky and window fixes, and more. Sale-ready listing photos from $0.40/image.',
    keywords: ['real estate photo enhancement', 'photo colour correction', 'image enhancement real estate', 'photo retouching'],
    priceFrom: '$0.40',
    priceUnit: 'per image',
    turnaround: '4–12 hours',
    formats: ['JPG', 'TIFF', 'PNG', 'RAW'],
    tagline: 'A comprehensive enhancement that makes every listing photo crisp, bright, and professional.',
    intro:
      'Image enhancement is our all-in-one polish: colour correction, white balance, exposure and contrast, vertical and horizontal straightening, sharpening, sky and window fixes, and minor blemish removal — everything a photo needs to look professional.',
    whyItMatters:
      'Even great shots need finishing. Enhancement gives every image in a shoot a consistent, polished, professional look that raises the perceived value of the property.',
    benefits: [
      { title: 'Colour & white balance', description: 'Accurate, natural colour across the whole set.', icon: '🎨' },
      { title: 'Straightening & cropping', description: 'Level horizons and correct verticals.', icon: '📐' },
      { title: 'Sky & window fixes', description: 'Blue skies and balanced window views.', icon: '🪟' },
      { title: 'Sharpening & cleanup', description: 'Crisp detail and blemish-free results.', icon: '✨' },
    ],
    beforeAfter: [
      { before: G('enhancement-exterior-before.webp'), after: G('enhancement-exterior-after.webp'), label: 'Exterior enhancement' },
      { before: G('enhancement-interior-before.webp'), after: G('enhancement-interior-after.webp'), label: 'Interior enhancement' },
      { before: G('2enbf.webp'), after: G('2enaf.webp'), label: 'Room enhancement' },
    ],
    faqs: [
      { q: 'What is included in photo enhancement?', a: 'Colour correction, white balance, exposure and contrast, straightening, sharpening, sky and window fixes, and minor blemish removal — a complete professional polish.' },
      { q: 'How is this different from HDR?', a: 'Enhancement improves a single photo. HDR blends multiple bracketed exposures. If you shoot brackets, choose HDR; if you shoot single frames, enhancement is ideal.' },
      { q: 'How fast is delivery?', a: 'Enhanced images are returned within 4–12 hours, with rush options available.' },
    ],
    related: ['hdr-photo-editing', 'item-removal', 'day-to-dusk', 'sky-replacement'],
    ogImage: G('enhancement-exterior-after.webp'),
    priority: 0.8,
  },

  'floor-plans': {
    slug: 'floor-plans',
    name: 'Floor Plan Conversion',
    h1: 'Floor plan conversion & redraws',
    metaTitle: 'Floor Plan Conversion & Redraw Services | Shashio',
    metaDescription:
      'Clean, branded 2D and 3D floor plans redrawn from sketches or measurements. Buyer-ready plans from $24.00 each with fast, accurate turnaround.',
    keywords: ['floor plan conversion', 'floor plan redraw', '2D floor plan', '3D floor plan real estate'],
    priceFrom: '$24.00',
    priceUnit: 'per plan',
    turnaround: '24–48 hours',
    formats: ['JPG', 'PNG', 'PDF', 'Hand sketch'],
    tagline: '2D and 3D floor plans redrawn clean, branded, and buyer-ready.',
    intro:
      'We redraw rough sketches, scans, or measured drawings into clean, professional floor plans — from simple black-and-white 2D layouts to fully rendered colour 3D plans, complete with dimensions, labels, and your branding.',
    whyItMatters:
      'Listings with floor plans get more engagement and higher-quality enquiries. Buyers understand the layout instantly, and agents look more professional.',
    benefits: [
      { title: '2D & 3D options', description: 'From clean black & white to full colour 3D.', icon: '📐' },
      { title: 'Branded & labelled', description: 'Dimensions, room labels, and your logo.', icon: '🏷️' },
      { title: 'From any source', description: 'Sketches, scans, or measured drawings.', icon: '✏️' },
      { title: 'Fast, accurate', description: 'Precise plans in 24–48 hours.', icon: '⚡' },
    ],
    beforeAfter: [
      { before: G('floorbf.webp'), after: G('flooraf.webp'), label: 'Sketch to floor plan' },
      { before: G('1fpbf.webp'), after: G('1fpaf.webp'), label: '2D floor plan' },
      { before: G('2fpbf.webp'), after: G('2fpaf.webp'), label: '3D floor plan' },
    ],
    faqs: [
      { q: 'What do you need to draw a floor plan?', a: 'A rough sketch, a scanned plan, or measurements. The clearer the source, the faster and more accurate the redraw.' },
      { q: 'Can I get a 3D floor plan?', a: 'Yes — we produce both clean 2D layouts and fully rendered 3D floor plans with furniture and colour.' },
      { q: 'Can you add my branding?', a: 'Yes. We add your logo, colours, room labels, and dimensions so the plan matches your marketing.' },
    ],
    related: ['virtual-staging', 'virtual-renovation', 'hdr-photo-editing', 'image-enhancement'],
    ogImage: G('flooraf.webp'),
    priority: 0.8,
  },

  'virtual-renovation': {
    slug: 'virtual-renovation',
    name: 'Virtual Renovation',
    h1: 'Virtual renovation & remodel previews',
    metaTitle: 'Virtual Renovation & Remodel Previews | Shashio',
    metaDescription:
      'Preview remodels before you build — new floors, walls, kitchens, landscaping, and full transformations. Photorealistic virtual renovation from $24.00/image.',
    keywords: ['virtual renovation', 'virtual remodel', 'renovation preview real estate', 'virtual makeover'],
    priceFrom: '$24.00',
    priceUnit: 'per image',
    turnaround: '48 hours',
    formats: ['JPG', 'TIFF', 'PNG'],
    tagline: 'Preview remodels before you build — new floors, walls, kitchens, landscaping, and more.',
    intro:
      'Virtual renovation shows a property’s potential by digitally remodelling it — updating floors, walls, cabinetry, fixtures, and landscaping so buyers and investors can see what a space could become.',
    whyItMatters:
      'Dated or damaged properties are hard to sell. Renovation previews help buyers see past the current condition and imagine the finished result, widening your pool of interested buyers.',
    benefits: [
      { title: 'Full remodels', description: 'Floors, walls, kitchens, bathrooms, and more.', icon: '🔨' },
      { title: 'Landscaping', description: 'Transform tired yards into inviting outdoor spaces.', icon: '🌳' },
      { title: 'Investor-ready', description: 'Show the after to justify the asking price.', icon: '📈' },
      { title: 'Photorealistic', description: 'Believable results matched to the original photo.', icon: '✨' },
    ],
    beforeAfter: [
      { before: G('1vfbf.webp'), after: G('1vfaf.webp'), label: 'Interior renovation' },
      { before: G('2vfbf.webp'), after: G('2vfaf.webp'), label: 'Kitchen remodel' },
      { before: G('ondorevsbefore.webp'), after: G('ondorevsafter.webp'), label: 'Full transformation' },
    ],
    faqs: [
      { q: 'What can you virtually renovate?', a: 'Floors, walls, paint, cabinetry, countertops, fixtures, and landscaping — from a single update to a full remodel.' },
      { q: 'Is this the same as virtual staging?', a: 'No. Staging adds furniture to an empty room; renovation changes the room itself — surfaces, structures, and finishes.' },
      { q: 'How long does it take?', a: 'Virtual renovations are typically delivered within 48 hours depending on complexity.' },
    ],
    related: ['virtual-staging', 'floor-plans', 'hdr-photo-editing', 'item-removal'],
    ogImage: G('1vfaf.webp'),
    priority: 0.8,
  },

  'sky-replacement': {
    slug: 'sky-replacement',
    name: 'Sky Replacement',
    h1: 'Sky replacement for real estate photos',
    metaTitle: 'Sky Replacement for Real Estate Photography | Shashio',
    metaDescription:
      'Swap dull, grey, or blown-out skies for clear blue or dramatic sunset skies that make exteriors pop. Natural sky replacement from $1.50/image in 4–12 hours.',
    keywords: ['sky replacement real estate', 'blue sky replacement', 'sky editing property photos', 'replace grey sky'],
    priceFrom: '$1.50',
    priceUnit: 'per image',
    turnaround: '4–12 hours',
    formats: ['JPG', 'TIFF', 'PNG', 'RAW'],
    tagline: 'Swap dull or grey skies for clear blue or dramatic sunset skies that make exteriors pop.',
    intro:
      'Sky replacement removes a flat, overcast, or blown-out sky and replaces it with a natural clear-blue or sunset sky — with matched lighting and reflections so the result looks like it was shot on a perfect day.',
    whyItMatters:
      'Weather doesn’t always cooperate on shoot day. Sky replacement guarantees a bright, appealing exterior every time, so grey skies never hold up a listing.',
    benefits: [
      { title: 'Natural blue skies', description: 'Believable skies matched to the scene.', icon: '☀️' },
      { title: 'Dramatic sunsets', description: 'Optional golden-hour skies for hero shots.', icon: '🌅' },
      { title: 'Matched lighting', description: 'Reflections and colour adjusted to fit.', icon: '💡' },
      { title: '4–12 hour turnaround', description: 'Never wait on the weather again.', icon: '⚡' },
    ],
    beforeAfter: [
      { before: G('enhancement-exterior-before.webp'), after: G('enhancement-exterior-after.webp'), label: 'Clear blue sky' },
      { before: G('2ddbf.webp'), after: G('2ddaf.webp'), label: 'Sunset sky' },
      { before: G('1ccbf.webp'), after: G('1ccaf.webp'), label: 'Exterior sky swap' },
    ],
    faqs: [
      { q: 'Will the sky look fake?', a: 'No. We match the new sky’s colour, direction of light, and reflections to the scene, and adjust the exterior lighting so the result looks completely natural.' },
      { q: 'Can I choose the type of sky?', a: 'Yes — pick a clear blue sky, a soft cloudy sky, or a dramatic sunset, or let our editors choose the best fit.' },
      { q: 'Does this work with HDR editing?', a: 'Yes. Sky replacement is often combined with HDR or day-to-dusk editing for a complete exterior hero shot.' },
    ],
    related: ['day-to-dusk', 'hdr-photo-editing', 'image-enhancement', 'item-removal'],
    ogImage: G('enhancement-exterior-after.webp'),
    priority: 0.8,
  },

  'portrait-retouching': {
    slug: 'portrait-retouching',
    name: 'Portrait Retouching',
    h1: 'Agent portrait & headshot retouching',
    metaTitle: 'Real Estate Agent Portrait Retouching | Shashio',
    metaDescription:
      'Professional headshot and portrait retouching for real estate agents — natural skin, clean backgrounds, and polished branding photos from $2.00/image.',
    keywords: ['portrait retouching', 'headshot retouching', 'agent headshot editing', 'real estate agent portrait'],
    priceFrom: '$2.00',
    priceUnit: 'per portrait',
    turnaround: '4–12 hours',
    formats: ['JPG', 'TIFF', 'PNG', 'RAW'],
    tagline: 'Polished, natural headshots that make agents look their professional best.',
    intro:
      'Portrait retouching gives real estate agents a clean, professional headshot — natural skin smoothing, blemish removal, stray-hair cleanup, teeth and eye enhancement, and background tidying or replacement.',
    whyItMatters:
      'An agent’s headshot is their personal brand — it appears on signs, cards, and every listing. A polished, natural portrait builds trust and recognition.',
    benefits: [
      { title: 'Natural skin', description: 'Smoothing that keeps texture — never plastic.', icon: '🙂' },
      { title: 'Blemish cleanup', description: 'Spots, stray hairs, and distractions removed.', icon: '✨' },
      { title: 'Background tidy', description: 'Clean up or replace the background entirely.', icon: '🖼️' },
      { title: 'Fast delivery', description: 'Retouched portraits within 4–12 hours.', icon: '⚡' },
    ],
    beforeAfter: [
      { before: G('portabef.webp'), after: G('portaaf.webp'), label: 'Agent headshot' },
    ],
    faqs: [
      { q: 'Will I still look like myself?', a: 'Yes. We keep retouching natural — clean and polished, never over-smoothed or artificial.' },
      { q: 'Can you replace the background?', a: 'Yes. We can tidy the existing background or swap it for a clean studio or branded backdrop.' },
      { q: 'How fast is delivery?', a: 'Retouched portraits are returned within 4–12 hours.' },
    ],
    related: ['image-enhancement', 'hdr-photo-editing', 'item-removal', 'virtual-staging'],
    ogImage: G('portaaf.webp'),
    priority: 0.7,
  },
};

/** Ordered list of services for nav, grids, and sitemap. */
export const SERVICE_LIST: SeoService[] = [
  SERVICES['hdr-photo-editing'],
  SERVICES['virtual-staging'],
  SERVICES['day-to-dusk'],
  SERVICES['item-removal'],
  SERVICES['image-enhancement'],
  SERVICES['sky-replacement'],
  SERVICES['floor-plans'],
  SERVICES['virtual-renovation'],
  SERVICES['portrait-retouching'],
];

export function getService(slug: string): SeoService | undefined {
  return SERVICES[slug];
}

export function serviceUrl(slug: string): string {
  return `${SITE_URL}/services/${slug}`;
}
